import { ref } from 'vue';
import { httpService, type ApiResponse } from '../services/httpService';
import { log, error as logError } from '../utils/DebugLogger';

/**
 * Factory composable for SO catalog CRUD operations.
 * @param {string} controllerName - Backend controller name (e.g., 'service_categories')
 * @returns CRUD methods + loading/error state
 */
export function useSOCatalog(controllerName: any) {
  const isLoading = ref(false);
  const error = ref(null);
  const basePath = `/index.php/so/${controllerName}`;

  const fetchAll = async (params?: Record<string, string | number>) => {
    log('useSOCatalog', 'fetchAll', `Fetching all ${controllerName}`, params);
    isLoading.value = true;
    error.value = null;
    try {
      let url = `${basePath}/lista`;
      if (params) {
        const qs = new URLSearchParams(
          Object.entries(params).reduce<Record<string, string>>((acc, [k, v]) => {
            acc[k] = String(v);
            return acc;
          }, {})
        ).toString();
        if (qs) url += `?${qs}`;
      }
      const response = await httpService.get<ApiResponse<unknown[]>>(url);
      if (response.success) {
        return response.dados || [];
      }
      throw new Error(response.error || `Erro ao buscar ${controllerName}`);
    } catch (err) {
      logError('useSOCatalog', 'fetchAll', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const getById = async (id: any) => {
    log('useSOCatalog', 'getById', `Fetching ${controllerName}`, { id });
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.get<ApiResponse>(`${basePath}/get/${id}`);
      if (response.success) {
        return response.dados;
      }
      throw new Error(response.error || `Erro ao buscar ${controllerName}`);
    } catch (err) {
      logError('useSOCatalog', 'getById', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const save = async (data: any) => {
    log('useSOCatalog', 'save', `Saving ${controllerName}`, data);
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.post<ApiResponse>(`${basePath}/salvar`, data);
      if (response.success) {
        return response;
      }
      throw new Error(response.error || `Erro ao salvar ${controllerName}`);
    } catch (err) {
      logError('useSOCatalog', 'save', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const remove = async (data: any) => {
    log('useSOCatalog', 'remove', `Removing ${controllerName}`, data);
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.post<ApiResponse>(`${basePath}/remover`, data);
      if (response.success) {
        return response;
      }
      throw new Error(response.error || `Erro ao remover ${controllerName}`);
    } catch (err) {
      logError('useSOCatalog', 'remove', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const fetchKV = async () => {
    log('useSOCatalog', 'fetchKV', `Fetching KV for ${controllerName}`);
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.get<ApiResponse>(`${basePath}/kv`);
      if (response.success) {
        return response.dados || {};
      }
      throw new Error(response.error || `Erro ao buscar KV de ${controllerName}`);
    } catch (err) {
      logError('useSOCatalog', 'fetchKV', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  return { isLoading, error, fetchAll, getById, save, remove, fetchKV };
}
